/**
 * H2 — hassas yetki talepleri: uçlar ve ekran metinleri (sözleşme `@castinflow/shared` `capability-approval.ts`).
 * Uçlar: `POST /users/:id/capability-requests` · `GET /capability-requests` · `POST /capability-requests/:id/{approve,reject,cancel}`.
 * L1: `POST /users/:id/capability-grants/owner-direct` (sahibin doğrudan onayı; gerekçe + yeniden kimlik doğrulama).
 */
import {
  CAPABILITY_APPROVAL_KIND_LABELS,
  CAPABILITY_LABELS,
  capabilityPolicySchema,
  capabilityRequestSchema,
  capabilityRequestsOverviewSchema,
  type CapabilityDecideBlock,
  type CapabilityName,
  type CapabilityOverride,
  type CapabilityPolicy,
  type CapabilityRequest,
  type CapabilityRequestsOverview,
  type OwnerDirectBlock,
  type UserRole,
} from '@castinflow/shared';
import { apiFetch } from '../../lib/api';
import { apiMutate } from '../../lib/session';

export const capabilityRequestKeys = {
  overview: (tenantId: string) => ['capability-requests', tenantId] as const,
  all: ['capability-requests'] as const,
};

export const fetchCapabilityRequests = async (): Promise<CapabilityRequestsOverview> =>
  capabilityRequestsOverviewSchema.parse(await apiFetch('/capability-requests'));

const one = (b: unknown): CapabilityRequest =>
  capabilityRequestSchema.parse((b as { request: unknown }).request);

export async function createCapabilityRequest(
  userId: string,
  body: {
    role?: UserRole;
    capabilities?: CapabilityOverride;
    reason: string;
    grantExpiresAt?: string;
    expected: { role: UserRole; override: CapabilityOverride };
  },
): Promise<CapabilityRequest> {
  return one(
    await apiMutate(`/users/${encodeURIComponent(userId)}/capability-requests`, 'POST', body),
  );
}

/** L1 — sahibin doğrudan onayı (tek adım). Yeniden kimlik doğrulama aynı istekte gider. */
export async function ownerDirectCapabilityGrant(
  userId: string,
  body: {
    role?: UserRole;
    capabilities?: CapabilityOverride;
    reason: string;
    grantExpiresAt?: string;
    expected: { role: UserRole; override: CapabilityOverride };
    reauth: { password: string; totpCode?: string };
  },
): Promise<CapabilityRequest> {
  return one(
    await apiMutate(
      `/users/${encodeURIComponent(userId)}/capability-grants/owner-direct`,
      'POST',
      body,
    ),
  );
}

/** L1 — çalışma alanının hassas yetki politikası (yalnız sahip; kapatmak yeniden kimlik doğrulama ister) */
export async function setCapabilityPolicy(body: {
  strictTwoPerson: boolean;
  reauth?: { password: string; totpCode?: string };
}): Promise<CapabilityPolicy> {
  return capabilityPolicySchema.parse(await apiMutate('/capability-policy', 'PUT', body));
}

export async function decideCapabilityRequest(
  id: string,
  action: 'approve' | 'reject' | 'cancel',
  note?: string,
): Promise<CapabilityRequest> {
  return one(
    await apiMutate(
      `/capability-requests/${encodeURIComponent(id)}/${action}`,
      'POST',
      action === 'cancel' ? {} : note === undefined || note === '' ? {} : { note },
    ),
  );
}

/** Süreli istisna seçenekleri (süre esaslı: seçim anından itibaren) */
export const GRANT_TERMS = [
  { id: 'none', label: 'Süresiz', ms: 0 },
  { id: '1d', label: '1 gün', ms: 86_400_000 },
  { id: '7d', label: '7 gün', ms: 7 * 86_400_000 },
  { id: '30d', label: '30 gün', ms: 30 * 86_400_000 },
  { id: '90d', label: '90 gün', ms: 90 * 86_400_000 },
] as const;
export type GrantTermId = (typeof GRANT_TERMS)[number]['id'];
export const grantExpiryOf = (id: GrantTermId, now = Date.now()): string | undefined => {
  const t = GRANT_TERMS.find((x) => x.id === id);
  return t === undefined || t.ms === 0 ? undefined : new Date(now + t.ms).toISOString();
};

const names = (c: readonly CapabilityName[]) => c.map((x) => CAPABILITY_LABELS[x]).join(', ');

export const reqText = {
  section: 'Yetki talepleri',
  sectionIntro:
    'Hassas yetkiler (ör. ayarlar, kullanıcı yönetimi, dışa aktarma, maliyet) iki yoldan biriyle verilir: çalışma alanı sahibinin doğrudan onayı (gerekçe ve yeniden kimlik doğrulamayla, kayıtta "sahibin doğrudan onayı" yazar) ya da talep eden dışında uygun bir yetkilinin onayı.',
  noRight: 'Yetki taleplerini kullanıcı yönetimi yetkisi olanlar görür ve karara bağlar.',
  inboxTitle: (n: number) => `Onayınızı bekleyen talepler (${String(n)})`,
  inboxEmpty: 'Onayınızı bekleyen yetki talebi yok',
  inboxEmptyBody:
    'Burada yalnız karar verebileceğiniz talepler görünür: kendi açtığınız ya da sizin için açılan talepler ve sahip olmadığınız yetkiyi isteyen talepler listelenmez.',
  mineTitle: (n: number) => `Açtığınız bekleyen talepler (${String(n)})`,
  mineEmpty: 'Açtığınız bekleyen talep yok.',
  historyTitle: 'Son kararlar',
  historyEmpty: 'Henüz karar verilmiş yetki talebi yok.',
  target: 'Kullanıcı',
  requestedBy: 'Talep eden',
  requestedAt: 'Talep',
  expiresAt: 'Onaylanmazsa düşer',
  grantUntil: (d: string) => `Süreli: ${d} tarihine kadar geçerli olur`,
  sensitive: (c: readonly CapabilityName[]) => `Hassas yetki: ${names(c)}`,
  added: (c: readonly CapabilityName[]) => `Eklenecek: ${names(c)}`,
  removed: (c: readonly CapabilityName[]) => `Kaldırılacak: ${names(c)}`,
  roleChange: (from: string, to: string) => `Rol: ${from} → ${to}`,
  reason: 'Gerekçe',
  note: 'Karar notu',
  noteHint: 'Ret için en az 3 karakter; onayda isteğe bağlı.',
  rejectNoteShort: 'Ret gerekçesi en az 3 karakter olmalı.',
  approve: 'Onayla',
  reject: 'Reddet',
  cancel: 'Talebi geri çek',
  approved: 'Talep onaylandı; yetki geçerli oldu',
  rejected: 'Talep reddedildi',
  cancelled: 'Talep geri çekildi',
  decidedBy: (n: string, d: string) => `${n} · ${d}`,
  approvers: (people: string) => `Onaylayabilecek kişiler: ${people}`,
  ownerTag: 'sahip',
  noApproverTitle: 'Bu talebi onaylayabilecek uygun kişi yok',
  noApproverBody: (until: string) =>
    `Talep ${until} tarihine kadar bekler, onaylanmazsa düşer; yetki o zamana kadar verilmez. Bağımsız onaylayan; talebi açan ve hedef kullanıcı dışında, etkin, kullanıcı yönetimi yetkisi ve istenen yetkilerin tamamı olan biri ya da çalışma alanı sahibi olmalıdır. Castinflow destek ekibi ve sunucu araçları yetki onayı veremez.`,
  /** L1: talebi sahip açtıysa ve onaylayan yoksa sahibin yolu */
  noApproverOwnerBody:
    'Çalışma alanı sahibi olarak bu talebi geri çekip yetkiyi kullanıcının "Yetkiler ve istisnalar" bölümünden "Doğrudan ver (sahip onayı)" ile verebilirsiniz; işlem gerekçe ve yeniden kimlik doğrulama ister ve kayıtta sahibin doğrudan onayı olarak görünür.',
  kind: CAPABILITY_APPROVAL_KIND_LABELS,
  blocked: {
    not_pending: 'Bu talep için karar verildi.',
    expired: 'Talebin süresi doldu.',
    self_requested: 'Kendi açtığınız talebi onaylayamazsınız; başka bir yetkili karar vermeli.',
    target_self: 'Sizin için açılan talebe karar veremezsiniz.',
    inactive: 'Hesabınız etkin değil.',
    no_users_manage: 'Karar için kullanıcı yönetimi yetkisi gerekir.',
    outranked: 'Bu kullanıcı sizden fazla yetkili; karar veremezsiniz.',
    lacks_capability: 'İstenen yetkilerin hepsine sahip değilsiniz; bu talebe karar veremezsiniz.',
  } satisfies Record<CapabilityDecideBlock, string>,
  errors: {
    request_pending: 'Bu kullanıcı için onay bekleyen bir talep var.',
    request_expired: 'Talebin süresi doldu; yeniden talep açılmalı.',
    not_pending: 'Bu talep için karar zaten verildi.',
    target_changed:
      'Kullanıcının yetkileri talepten sonra değişti; talebi reddedin ya da geri çekip yeniden açın.',
    capability_exceptions_changed: 'Yetkiler bu arada değişti; güncel hâl yüklendi.',
    approval_not_required: 'Bu değişiklik onay gerektirmiyor.',
    owner_direct_owner_only:
      'Hassas yetkiyi doğrudan yalnız çalışma alanı sahibi verebilir; onaya gönderin.',
    strict_two_person_policy:
      'Bu çalışma alanında hassas yetkiler her zaman iki kişili onayla verilir; onaya gönderin.',
    reauth_required: 'Parolanızı girin (iki adımlı doğrulama açıksa kodunuzu da).',
    owner_protected: 'Çalışma alanı sahibinin yetkileri değiştirilemez.',
    policy_owner_only: 'Bu politikayı yalnız çalışma alanı sahibi değiştirebilir.',
    policy_changed: 'Politika bu arada değişti; güncel hâl yüklendi.',
    self_change: 'Kendi yetkilerinizi değiştiremezsiniz.',
    grant_term_out_of_range: 'Süre en az 15 dakika, en çok 365 gün olmalı.',
    failed: 'İşlem tamamlanamadı.',
    forbidden: 'Bu işlem için yetkiniz yok.',
  },
  pendingOnUser: 'Onay bekleyen yetki talebi',
  pendingOnUserBody:
    'Bu kullanıcı için açık bir talep var. Talep karara bağlanana ya da geri çekilene kadar yetkiler değiştirilemez.',
  openCenter: 'Onay merkezinde aç',
  status: {
    pending: 'Onay bekliyor',
    approved: 'Onaylandı',
    rejected: 'Reddedildi',
    cancelled: 'Geri çekildi',
    expired: 'Süresi doldu',
  },
};

/** L1 — "Doğrudan ver (sahip onayı)" metinleri (kullanıcının yetki istisnaları bölümü) */
export const ownerDirectText = {
  action: 'Doğrudan ver (sahip onayı)',
  intro:
    'Çalışma alanı sahibi olarak bu yetkiyi tek adımda verebilirsiniz. Kimliğinizi yeniden doğrularsınız; kayıtta ve geçmişte işlem "sahibin doğrudan onayı" olarak görünür (iki ayrı kişinin onayı gibi gösterilmez).',
  passwordHint: 'Hassas yetkiyi doğrudan vermek için parolanızı yeniden girin.',
  confirm: 'Doğrula ve ver',
  cancel: 'Vazgeç',
  done: (names: string) =>
    `${names} sahibin doğrudan onayıyla verildi ve hemen geçerli oldu. Kayıt denetim geçmişine yazıldı.`,
  failed: 'Kimlik doğrulanamadı: parolanızı ve doğrulama kodunuzu denetleyin.',
  blocked: {
    not_owner:
      'Hassas yetkiyi doğrudan yalnız çalışma alanı sahibi verebilir; sizin değişikliğiniz onaya gider ve sizden başka uygun bir yetkili onaylar.',
    strict_policy:
      'Bu çalışma alanında "her zaman iki kişili onay" politikası açık: hassas yetki onaya gider.',
    target_owner: '',
    self: '',
  } satisfies Record<OwnerDirectBlock, string>,
};

export function requestErrorText(e: unknown): string {
  const err = e as { status?: number; code?: string; details?: { reason?: string } } | null;
  if (err?.code === 'UNAUTHENTICATED') return ownerDirectText.failed;
  const r = err?.details?.reason;
  if (r !== undefined && r in reqText.errors)
    return reqText.errors[r as keyof typeof reqText.errors];
  if (r !== undefined && r in reqText.blocked) return reqText.blocked[r as CapabilityDecideBlock];
  if (err?.status === 403) return reqText.errors.forbidden;
  return reqText.errors.failed;
}
